export const conditionsData = {
  // Pain Conditions
  "back-pain": {
    title: "Back Pain",
    category: "Pain Conditions",
    overview: "Chronic back pain is a persistent condition that can significantly impact daily life, mobility, and overall well-being. It is one of the most common reasons for long-term absence from work in the UK and often persists despite standard medical interventions and physical therapies.",
    clinicalContext: "This page provides general information about chronic back pain. Patients with a confirmed diagnosis of chronic back pain who have tried at least two conventional treatments without adequate benefit may be considered for specialist clinical assessment at Integro. Whether treatment is clinically appropriate is determined by the prescribing clinician following individual assessment and MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Persistent aching or stiffness",
      "Sharp, localised pain",
      "Radiating pain to the legs",
      "Muscle spasms",
      "Limited flexibility"
    ]
  },
  "fibromyalgia": {
    title: "Fibromyalgia",
    category: "Pain Conditions",
    overview: "Fibromyalgia is a long-term condition that causes widespread pain throughout the body. Patients often experience increased sensitivity to pain, extreme tiredness, and muscle stiffness. It is estimated to affect around 2% of the UK population and can be difficult to manage with conventional medication.",
    clinicalContext: "This page provides general information about fibromyalgia. Patients with a confirmed diagnosis who have tried at least two conventional treatments without adequate benefit may be considered for specialist clinical assessment. Whether a treatment is clinically appropriate is determined by the prescribing clinician following individual assessment and MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Widespread muscle pain",
      "Extreme fatigue",
      "Sleep disturbances",
      "Cognitive difficulties ('fibro-fog')",
      "Headaches"
    ]
  },
  "arthritis": {
    title: "Arthritis",
    category: "Pain Conditions",
    overview: "Arthritis involves the swelling and tenderness of one or more joints. The main symptoms are joint pain and stiffness, which typically worsen with age. There are many types of arthritis, the most common being osteoarthritis and rheumatoid arthritis.",
    clinicalContext: "This page provides general information about arthritis. Patients with a confirmed diagnosis of arthritis who have not responded adequately to conventional treatments may be considered for specialist clinical assessment. Treatment decisions are made by the prescribing clinician based on the patient's individual circumstances, following MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Joint pain and tenderness",
      "Stiffness and swelling",
      "Reduced range of motion",
      "Redness near joints",
      "Warmth in affected areas"
    ]
  },
  "cancer-related-pain": {
    title: "Cancer-Related Pain",
    category: "Pain Conditions",
    overview: "Pain associated with cancer or its treatment can be severe and persistent. It can result from the tumour itself, or from the side effects of chemotherapy, radiotherapy, or surgery. Cancer-related pain is a recognised area where specialist pain management may be considered.",
    clinicalContext: "This page provides general information about cancer-related pain. Patients experiencing pain related to cancer or its treatment may be considered for specialist clinical assessment where conventional pain management has not provided adequate relief. Treatment decisions are made by the prescribing clinician following individual clinical assessment and MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Severe, persistent pain",
      "Nerve pain (neuropathy)",
      "Bone pain",
      "Nausea and vomiting",
      "Loss of appetite"
    ]
  },

  // Neurological
  "migraine": {
    title: "Migraine",
    category: "Neurological",
    overview: "Migraines are severe, recurring headaches often accompanied by sensory warning signs. They can be debilitating and last for hours or even days. Migraines affect approximately one in seven people in the UK and can significantly impact quality of life.",
    clinicalContext: "This page provides general information about migraine. Patients with a confirmed diagnosis of chronic migraine who have tried at least two conventional treatments without adequate benefit may be considered for specialist clinical assessment at Integro. Whether treatment is clinically appropriate is determined following individual assessment and MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Severe throbbing pain",
      "Sensitivity to light and sound",
      "Nausea",
      "Visual disturbances (aura)",
      "Dizziness"
    ]
  },
  "ms": {
    title: "Multiple Sclerosis (MS)",
    category: "Neurological",
    overview: "Multiple Sclerosis (MS) is a condition in which the immune system attacks the protective myelin sheath covering nerve fibres, disrupting communication between the brain and body. MS affects around 130,000 people in the UK. Symptoms vary widely between individuals.",
    clinicalContext: "This page provides general information about multiple sclerosis. Sativex (nabiximols), a cannabis-based medicine, is licensed in the UK for the treatment of spasticity in MS where other treatments have not been adequate, as recognised by the NHS and NICE. Patients may be considered for specialist clinical assessment on an individual basis. Treatment decisions are made by the prescribing clinician following assessment and MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Muscle stiffness and spasms",
      "Fatigue",
      "Pain",
      "Vision problems",
      "Balance issues"
    ]
  },
  "parkinsons": {
    title: "Parkinson's Disease",
    category: "Neurological",
    overview: "Parkinson's disease is a progressive neurological disorder that affects movement. It is caused by a loss of nerve cells in the part of the brain that coordinates movement. Symptoms develop gradually and can vary significantly between individuals.",
    clinicalContext: "This page provides general information about Parkinson's disease. Patients with a confirmed diagnosis may be considered for specialist clinical assessment at Integro in appropriate cases. Treatment decisions are made by the prescribing clinician based on the patient's individual circumstances and clinical history, following MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Tremors",
      "Slowed movement (bradykinesia)",
      "Rigid muscles",
      "Impaired posture and balance",
      "Speech changes"
    ]
  },

  // Women's Health
  "endometriosis": {
    title: "Endometriosis",
    category: "Women's Health",
    overview: "Endometriosis is a condition where tissue similar to the lining of the womb grows in other places, such as the ovaries and fallopian tubes. It is a long-term condition that can affect women of any age and is often associated with significant chronic pain.",
    clinicalContext: "This page provides general information about endometriosis. Patients with a confirmed diagnosis of endometriosis who have tried conventional treatments without adequate benefit may be considered for specialist clinical assessment. Treatment decisions are made by the prescribing clinician following individual clinical assessment and MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Severe pelvic pain",
      "Painful periods",
      "Pain during intercourse",
      "Excessive bleeding",
      "Fatigue"
    ]
  },
  "menopause": {
    title: "Menopause",
    category: "Women's Health",
    overview: "Menopause marks the end of menstrual cycles and is diagnosed after 12 months without a period. The associated hormonal changes can trigger a range of physical and emotional symptoms that can significantly disrupt daily life.",
    clinicalContext: "This page provides general information about menopause-related symptoms. Patients experiencing menopause-related symptoms that have not responded adequately to conventional treatments may be considered for specialist clinical assessment. Treatment decisions are made by the prescribing clinician following individual assessment and MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Hot flushes",
      "Sleep problems",
      "Mood changes",
      "Anxiety",
      "Joint pain"
    ]
  },
  "pmdd": {
    title: "Pre-Menstrual Dysphoric Disorder (PMDD)",
    category: "Women's Health",
    overview: "PMDD is a severe form of premenstrual syndrome (PMS) that causes significant emotional and physical symptoms in the one to two weeks before a period. It is a recognised medical condition that can substantially impact quality of life.",
    clinicalContext: "This page provides general information about PMDD. Patients with a confirmed diagnosis who have tried conventional treatments without adequate benefit may be considered for specialist clinical assessment at Integro. Treatment decisions are made by the prescribing clinician following individual assessment and MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Severe irritability",
      "Depression or anxiety",
      "Mood swings",
      "Fatigue",
      "Physical pain and cramping"
    ]
  },

  // Psychiatric / Neurodevelopmental
  "ptsd": {
    title: "Post-Traumatic Stress Disorder (PTSD)",
    category: "Psychiatric / Neurodevelopmental",
    overview: "Post-Traumatic Stress Disorder (PTSD) is a mental health condition that can develop after experiencing or witnessing a traumatic event. It is characterised by intrusive memories, hyperarousal, avoidance behaviours, and significant changes in mood and cognition. PTSD can be severely disabling and does not always respond adequately to first-line treatments.",
    clinicalContext: "This page provides general information about PTSD. Patients with a confirmed diagnosis of PTSD who have tried at least two conventional treatments without adequate benefit may be considered for specialist clinical assessment at Integro. Treatment decisions are made by the prescribing clinician following individual assessment and MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Intrusive memories or flashbacks",
      "Nightmares and sleep disturbance",
      "Hypervigilance and exaggerated startle response",
      "Avoidance of trauma-related stimuli",
      "Emotional numbing and detachment"
    ]
  },
  "autism": {
    title: "Autism Spectrum Disorder (ASD)",
    category: "Psychiatric / Neurodevelopmental",
    overview: "Autism Spectrum Disorder (ASD) is a lifelong neurodevelopmental condition affecting how people communicate, interact, and process information. Many autistic individuals experience co-occurring conditions such as anxiety, sleep difficulties, and sensory sensitivities that can significantly impact quality of life and may not be adequately managed by standard interventions.",
    clinicalContext: "This page provides general information about autism spectrum disorder. At Integro, assessment focuses on co-occurring symptoms such as anxiety, sleep disturbance, or significant behavioural challenges in autistic individuals where conventional treatments have not provided adequate benefit. Suitability for treatment is determined individually by the clinical team following thorough assessment. Treatment decisions are made by the prescribing clinician following individual clinical assessment and MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Difficulties with social communication and interaction",
      "Repetitive behaviours or restricted interests",
      "Sensory sensitivities",
      "Co-occurring anxiety or sleep disturbance",
      "Challenges with transitions or changes in routine"
    ]
  },
  "adhd": {
    title: "Attention Deficit Hyperactivity Disorder (ADHD)",
    category: "Psychiatric / Neurodevelopmental",
    overview: "Attention Deficit Hyperactivity Disorder (ADHD) is a neurodevelopmental condition characterised by persistent patterns of inattention, hyperactivity, and impulsivity that can interfere with daily functioning. It affects adults and may co-occur with other conditions including sleep difficulties, anxiety, and mood disorders.",
    clinicalContext: "This page provides general information about ADHD. At Integro, assessment considers ADHD within the broader clinical context, particularly where co-occurring symptoms such as sleep difficulties or other qualifying presentations have not responded adequately to conventional treatments. Suitability is determined individually by the clinical team. Treatment decisions are made by the prescribing clinician following individual clinical assessment and MDT review. Eligibility does not guarantee that treatment will be recommended or prescribed.",
    symptoms: [
      "Persistent inattention and difficulty concentrating",
      "Hyperactivity and restlessness",
      "Impulsivity and difficulty regulating behaviour",
      "Co-occurring sleep difficulties",
      "Emotional dysregulation"
    ]
  },

  // Sleep
  "sleep-insomnia": {
    title: "Sleep & Insomnia",
    category: "Sleep",
    overview: "Sleep difficulties and insomnia are among the most common health complaints in the UK. Persistent sleep problems can affect physical health, mental wellbeing, mood, concentration, and quality of life. They frequently occur alongside other chronic conditions, including persistent pain, neurological conditions, and other long-term health problems.",
    clinicalContext: "This page provides general information about sleep difficulties and insomnia. At Integro, sleep disturbance is often assessed as part of a broader clinical picture, particularly where it occurs alongside a diagnosed chronic condition such as persistent pain. Suitability for treatment is determined individually by the clinical team following a thorough assessment of your medical history and current presentations. Completing an eligibility check does not guarantee a prescription. Treatment decisions are made by the prescribing clinician following individual clinical assessment and MDT review.",
    symptoms: [
      "Difficulty falling or staying asleep",
      "Frequent waking during the night",
      "Unrefreshing or non-restorative sleep",
      "Daytime fatigue and impaired concentration",
      "Mood disturbances related to poor sleep"
    ]
  },
};
